/*!
  Pioneer WFRIA MultiSelect v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MultiSelect"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MultiSelect") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MultiSelect");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["children", "className", "customValidators", "disabled", "expandIconSize", "id", "label", "ariaLabel", "required", "onBlur", "menuzIndex", "noAsterisk", "hint", "labelHidden", "name", "selectAllOption", "disabledItems", "selectedItems", "defaultSelectedItems", "onSelectedChange", "instructionalText", "customStrings", "multiSelectMenuWidth", "wrap"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { KEYBOARD_KEYS, KEYBOARD_NAVIGATOR_MODE, MENUITEM, MENUITEMGROUP, ZINDEX, ZOOM_400_MAX_WIDTH } from "../common/constants";
import { useFocusFieldOnError, useOnEscape, useUniqueId } from "../common/hooks";
import { requiredIf } from "../common/proptypes";
import { __uniqueID, isActiveElementFocusable, registerField, safeMerge } from "../common/utilities";
import { FormContext } from "../Form/FormContext";
import { KeyboardNavigator, Popup } from "../private";
import FieldFooter from "../private/FieldFooter/FieldFooter";
import OptGroup from "../private/OptGroup";
import { OVERRIDE, SELECT_BACKGROUND_SCROLL_CLASS } from "../themes/common/classes";
import { NINETY_PERCENTAGE, REQUIRED_TEXT, RESIZE_DEBOUNCE_TIME, SCROLL_ATTACH_TIME, SCROLL_THROTTLE_TIME, SEARCH_DEBOUNCE_TIME } from "./constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, IconArrowClose, IconExpand, IconRequired, MenuItem } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function MultiSelectFooter(_ref) {
  let {
    displayErrors,
    errors,
    errorId,
    footerErrorStyle,
    footerStyle,
    hint,
    hintId,
    label,
    name,
    hintCSS,
    open
  } = _ref;
  let fieldFooterProps = null;

  if (displayErrors && errors[name]) {
    fieldFooterProps = {
      displayErrors,
      label,
      footerErrorStyle,
      message: errors[name].message,
      id: errorId
    };
  } else if (hint) {
    fieldFooterProps = {
      id: hintId,
      hint,
      css: hintCSS
    };
  }

  return fieldFooterProps && !open ? _jsx(FieldFooter, _objectSpread(_objectSpread({}, fieldFooterProps), {}, {
    footerStyle: footerStyle
  })) : null;
}
/**
 * The MultiSelect component is a custom implementation of native `<select multiple>` form input element
 * with checkboxes and an added functionality to select grouped items.
 * It is basically a dropdown which allows the user to select multiple options from a list of static data.
 * It can contain optional instruction text which provides the user with selection rules.
 */


const MultiSelect = /*#__PURE__*/React.forwardRef((props, userRef) => {
  const {
    children,
    className,
    customValidators,
    // eslint-disable-line no-unused-vars
    disabled: isDisabled,
    expandIconSize,
    id: userPassedId,
    label,
    ariaLabel,
    // eslint-disable-line no-unused-vars
    // nativeValidationMessages,
    required,
    onBlur,
    menuzIndex,
    noAsterisk,
    hint,
    labelHidden,
    // eslint-disable-line no-unused-vars
    name,
    selectAllOption,
    // eslint-disable-line no-unused-vars
    disabledItems,
    // eslint-disable-line no-unused-vars
    selectedItems: selectedItemsProp,
    // eslint-disable-line no-unused-vars
    defaultSelectedItems,
    // eslint-disable-line no-unused-vars
    onSelectedChange,
    // eslint-disable-line no-unused-vars
    instructionalText,
    // eslint-disable-line no-unused-vars
    customStrings,
    // eslint-disable-line no-unused-vars
    multiSelectMenuWidth,
    // eslint-disable-line no-unused-vars
    wrap
  } = props,
        elementProps = _objectWithoutProperties(props, _excluded);

  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [selectedItems, setInternalSelectedItems] = useState([]); // isBlurred is used to force a re-render for AT error announcement.

  const [isBlurred, setIsBlurred] = useState(false);
  const {
    register,
    isSubmitting,
    errors = {},
    resetDone,
    setValue,
    trigger,
    unregister,
    validateTimestamp
  } = useContext(FormContext) || {};
  /**
   * Errors should be first shown on blur, and then on change.
   * This state "displayErrors" is used to that effect: even
   * if the Form is reporting errors, we do not show them
   * before onBlur is triggered and change this state.
   *
   * This state is also reverted back to false on reset.
   */

  const [displayErrors, setDisplayErrors] = useState(false);
  const uniqueId = useUniqueId();
  const theme = useTheme();
  const wrapperNodeRef = useRef();
  const labelRef = useRef();
  const selectRef = useRef();
  const popupRef = useRef();
  const keyboardNavigatorRef = useRef();
  const triggerRef = useRef();
  const thisRef = useRef({
    currentIndex: 0,
    allEnabledItemValues: [],
    allItemsCount: 0,
    itemsMap: {},
    balloonNode: null,
    triggerNode: null,
    activeElementFocusable: false,
    itemDisabledCount: 0,
    groupItemDisabledCount: 0,
    listBoxRef: null,
    searchQuery: "",
    scrollListenerTimeout: null,
    scrollCount: 0,
    menuItemGroupIds: []
  });
  const popupId = `pnr-multiselect-popup-${uniqueId}`;
  const labelId = `pnr-multiselect__label-${uniqueId}`;
  const triggerId = `pnr-multiselect__trigger-${uniqueId}`;
  const errorId = `pnr-multiselect-error-${uniqueId}`;
  const containerClassNames = clsx(className, "pnr-multiselect");
  const instructionalTextId = `pnr-multiselect__instructional-text-${uniqueId}`;
  const hintId = `pnr-select-hint-${uniqueId}`;
  const labelClassNames = clsx({
    "wf-u-visually-hidden": labelHidden
  });
  useFocusFieldOnError(selectRef, triggerRef, isSubmitting);
  /**
   * getElementWidth method returns the width of the element
   * @param {el} The element node
   * @return {width: Number}
   */

  const getElementWidth = el => {
    const {
      width
    } = el.getBoundingClientRect();
    return {
      width
    };
  };

  const {
    multiSelectRoot,
    multiSelectWrapperStyles,
    multiSelectMenuCSS,
    hintCSS,
    multiSelectLabelCSS,
    multiSelectLabelInlineCSS,
    buttonCSS,
    buttonErrorCSS,
    multiSelectFooterCSS,
    multiSelectFooterErrorCSS,
    multiSelectLabelErrorCSS,
    multiSelectOpenedCSS,
    menuItemGroupCSS,
    menuItemCSS,
    menuItemWrapCSS,
    menuItemSelectedCSS,
    menuItemDisabledCSS,
    instructionalTextCSS
  } = extractTheme({
    theme
  });
  const {
    floatingLabel
  } = multiSelectRoot;

  const selectButtonStyles = _objectSpread(_objectSpread({}, !open && errors[name] && displayErrors ? safeMerge(buttonCSS, buttonErrorCSS) : buttonCSS), open ? multiSelectOpenedCSS : []);

  const labelStyles = _objectSpread(_objectSpread(_objectSpread({}, multiSelectLabelInlineCSS), !open && errors[name] && displayErrors ? safeMerge(multiSelectLabelCSS, multiSelectLabelErrorCSS) : multiSelectLabelCSS), isDisabled ? multiSelectLabelCSS.disabled : []);

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    itemCountText,
    requiredErrorMessage
  } = componentStrings;

  const getLabel = _jsxs("label", {
    id: labelId,
    "aria-hidden": "true",
    htmlFor: triggerId,
    className: labelClassNames,
    css: labelStyles,
    ref: labelRef,
    children: [required && !labelHidden && !noAsterisk && !floatingLabel ? _jsx(IconRequired, {
      display: "inline-block",
      size: "xsmall",
      spacing: {
        r: 0.5
      }
    }) : null, label]
  });
  /*
   * This method is responsible for explicitly
   * focusing on a selectable item when its
   * either selected/unselected.
   *
   * @param e {EventFacade} The event object
   */


  const focusOnSelectableItem = e => {
    /**
     * JAWS doesn't convey that the item is selected/unselected.
     * In order for JAWS to read the selected/unselected state of the item,
     * explicitly focusing on the trigger node first to trigger blur event
     * on the selectable item and then refocusing on the selectable item.
     */
    thisRef.current.triggerNode && thisRef.current.triggerNode.focus();
    const currentTarget = e && e.currentTarget;
    currentTarget && currentTarget.focus && currentTarget.focus();
  };
  /**
   * Fires the `onSelectedChange` callback if provided in controlled mode.
   * Sets the value of `selectedItems` to state in uncontrolled mode.
   * Sets `selectedIndex` on native select element to the first selected
   * option in the list of selected items because IE needs this to
   * recognize that a value has been selected.
   */


  const updateSelectedItems = value => {
    const {
      selectedItems,
      onSelectedChange
    } = props;
    const selectOptions = selectRef.current.options;
    selectRef.current.selectedIndex = -1;

    for (let i = 0; i < selectOptions.length; i++) {
      selectOptions[i].selected = false;

      if (value.indexOf(selectOptions[i].value) > -1) {
        if (selectRef.current.selectedIndex === -1) {
          selectRef.current.selectedIndex = i;
        }

        selectOptions[i].selected = true;
      }
    }

    selectedItems ? onSelectedChange && onSelectedChange(value) : setInternalSelectedItems(value); // Dispatch change event to trigger `handleNativeSelectOnChange`.

    const changeEvent = new Event("change");
    selectRef.current.dispatchEvent(changeEvent);
  };

  const handleSelectAll = () => {
    const disabledSelectedItems = disabledItems && selectedItems.filter(item => disabledItems.includes(item));
    const disabledItemsExists = (thisRef.current.itemDisabledCount || thisRef.current.groupItemDisabledCount) > 0; // Total disabled items count from MenuItem and MenuItemGroup.

    const disabledItemCount = thisRef.current.itemDisabledCount + thisRef.current.groupItemDisabledCount;
    const disabledSelectedItemCount = disabledSelectedItems ? disabledSelectedItems.length : 0;
    /**
     * As Select All checkbox will be checked/unchecked based on only enabled item checkbox.
     * Get the selected checkbox item count excluding disabled items.
     */

    const isCurrentlySelected = disabledItemsExists ? thisRef.current.allItemsCount - disabledItemCount === selectedItems.length - disabledSelectedItemCount : thisRef.current.allItemsCount === selectedItems.length;

    if (isCurrentlySelected) {
      // Unselect all rows, except the disabled selected items.
      const items = disabledSelectedItems ? disabledSelectedItems : [];
      updateSelectedItems(items);
    } else {
      // Select all rows.
      const items = disabledSelectedItems ? [...thisRef.current.allEnabledItemValues, ...disabledSelectedItems] : thisRef.current.allEnabledItemValues;
      updateSelectedItems(items);
    }
  };

  const handleSelection = (e, value) => {
    if (value === componentStrings.selectAllText) {
      handleSelectAll();
      focusOnSelectableItem(e);
      return;
    } // const { selectedItems } = this.state;


    const currentSelectedItems = [...selectedItems];
    const isCurrentlySelected = selectedItems.includes(value);

    if (isCurrentlySelected) {
      // Unselect row.
      currentSelectedItems.splice(currentSelectedItems.indexOf(value), 1);
    } else {
      currentSelectedItems.push(value);
    }

    focusOnSelectableItem(e);
    updateSelectedItems(currentSelectedItems);
  };

  const handleGroupClick = (isGroupCurrentlySelected, childrenValues) => {
    const currentSelectedItems = [...selectedItems];
    const isGroupIndeterminate = childrenValues.some(ele => currentSelectedItems.includes(ele)); //  if group already is selected, unselect itself and all its children.

    if (isGroupCurrentlySelected || isGroupIndeterminate) {
      const filteredItems = currentSelectedItems.filter(item => !childrenValues.includes(item));
      setInternalSelectedItems(filteredItems);
    } //  else select itself and all its children.
    else {
      const items = [...selectedItems, ...childrenValues];
      setInternalSelectedItems(items);
    }
  };

  const setBalloonRef = el => {
    thisRef.current.balloonNode = el && el.contentRef.current;
    popupRef.current = el;
  };
  /**
   * Clones MenuItem and wraps with KeyboardNavigator.
   * Adds the selected and disabled class.
   * Sets activeElement index for KeyboardNavigator.
   *
   * @param {object} child This holds the value, label and disabled property of the MenuItem.
   * @param {number} focusIndex This is used to set the activeElement index for the KeyboardNavigator.
   * @param {boolean} nestedItem Determines whether to add the sub account icon for a nested list item.
   * @param {number} groupId This id has to be passed to nested MenuItem's aria-describedby.
   * @param {object} menuItemsExtentions This holds additional props to be addeed to the MenuItem.
   * @return {React.Element} Returns MenuItem wrapped inside Keyboard Navigator item.
   */


  const cloneMenuItem = (child, focusIndex, nestedItem, groupId, menuItemExtensions) => {
    const {
      wrap,
      disabledItems
    } = props;
    const {
      value: menuItemValue,
      children: label,
      disabled: disabledViaMenuItem,
      wrap: wrapItem,
      className: menuItemClassName
    } = child.props;
    /**
     * The item wrap prop always takes precedence over parent wrap prop.
     */

    let shouldWrap = null;

    if (wrap || wrapItem) {
      shouldWrap = !(wrapItem === false);
    }

    const isDisabled = Boolean(disabledViaMenuItem || disabledItems && disabledItems.includes(menuItemValue));
    const isItemSelected = selectedItems && selectedItems.includes(menuItemValue);

    const menuItemWrapperStyles = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, menuItemCSS), shouldWrap ? menuItemWrapCSS : []), isItemSelected && !isDisabled ? menuItemSelectedCSS : []), isDisabled ? menuItemDisabledCSS : []), nestedItem ? menuItemGroupCSS : []);

    const allItemsSelected = thisRef.current.allItemsCount - thisRef.current.itemDisabledCount - thisRef.current.groupItemDisabledCount === selectedItems.length;

    const isIndeterminate = () => {
      /*
       * Only pass indeterminate value for "Select All" MenuItem when
       * at least one item is selected but not all items are selected.
       */
      if (menuItemValue === componentStrings.selectAllText && selectedItems.length > 0 && !allItemsSelected) {
        return true;
      }

      return false;
    };

    const isSelected = () => {
      if (menuItemValue === componentStrings.selectAllText && allItemsSelected) {
        return true;
      }

      return selectedItems && selectedItems.includes(menuItemValue);
    };

    const extensions = _objectSpread(_objectSpread({}, menuItemExtensions), {}, {
      key: menuItemValue,
      value: menuItemValue,
      onSelect: e => {
        if (child.props.onSelect) {
          child.props.onSelect(e, menuItemValue);
        }

        handleSelection(e, menuItemValue);
      },
      role: "option",
      className: `${menuItemClassName}`,
      keyboardNavigateListItem: true,
      wrap: shouldWrap,
      withCheckbox: true,
      isIndeterminate: isIndeterminate(),
      selectedValue: isSelected() ? menuItemValue : null,
      // tabIndex: "-1",
      "aria-selected": Boolean(isSelected()),
      "aria-label": isSelected() ? `${label} ${componentStrings.selectedText}` : `${label}`,
      disabled: isDisabled,
      "aria-disabled": isDisabled,
      "aria-hidden": isDisabled
    });

    if (nestedItem) {
      extensions.children = [label];
    }

    extensions.menuItemStyles = {
      menuItemWrapperCSS: menuItemWrapperStyles
    };
    const clonedElement = /*#__PURE__*/React.cloneElement(child, extensions);

    if (isDisabled) {
      return clonedElement;
    } else {
      thisRef.current.itemsMap[menuItemValue] = label;
      return clonedElement;
    }
  };

  const getUniqueGroupId = groupItemIndex => {
    let generatedId = null;
    const menuItemGroupIds = [...thisRef.current.menuItemGroupIds];

    if (menuItemGroupIds[groupItemIndex]) {
      generatedId = menuItemGroupIds[groupItemIndex];
    } else {
      generatedId = `pnr-multiselect__group-${__uniqueID()}`;
      menuItemGroupIds.push(generatedId);
      thisRef.current.menuItemGroupIds = menuItemGroupIds;
    }

    return generatedId;
  };

  const getListFromChildren = () => {
    const {
      selectAllOption,
      instructionalText
    } = props;
    const {
      selectAllText,
      groupAriaLabel
    } = componentStrings;
    const listItems = {
      liList: [],
      optionsList: []
    };
    let focusIndex = -1,
        disabledMenuItemCount = 0,
        menuItemGroupIndex = 0,
        childrenCount = React.Children.count(children);
    thisRef.current.allEnabledItemValues = [];
    thisRef.current.allItemsCount = 0;
    thisRef.current.itemsMap = {};
    const allItems = [];

    if (instructionalText) {
      const itemElementProps = {
        kind: "simple",
        role: null,
        disabled: true,
        tabIndex: "-1"
      };

      const instructionalMenuItem = _jsx(MenuItem, {
        value: "instructionaltext",
        disabled: true,
        itemElementProps: itemElementProps,
        tabIndex: "-1",
        "aria-hidden": "true",
        id: instructionalTextId,
        menuItemStyles: {
          menuItemWrapperCSS: instructionalTextCSS
        },
        children: instructionalText
      }, "instructionaltext");

      listItems.liList.push(instructionalMenuItem);
      allItems.push({
        label: instructionalText,
        isDisabled: true
      });
    }

    const isMenuItemIsDisabled = (value, disabledViaMenuItem) => {
      const {
        disabledItems
      } = props;
      if (disabledViaMenuItem) return true;
      if (disabledItems && disabledItems.includes(value)) return true;
      return false;
    };

    if (selectAllOption) {
      childrenCount++;
    }
    /**
     * Index is 0 indexed, aria-posinset should be 1 indexed.
     */


    const menuItemAriaSetSize = selectAllOption ? childrenCount - 1 : childrenCount;
    React.Children.map(children, (child, index) => {
      const childType = child.type;

      if (childType.displayName === MENUITEM) {
        const {
          value,
          disabled: disabledViaMenuItem,
          children
        } = child.props;
        const isDisabled = isMenuItemIsDisabled(value, disabledViaMenuItem); // increase focus index only when the list item is enabled

        if (!isDisabled) {
          focusIndex++;
          thisRef.current.allEnabledItemValues.push(value);
        } else {
          disabledMenuItemCount++;
          thisRef.current.itemDisabledCount = disabledMenuItemCount;
        }

        thisRef.current.allItemsCount++;
        const menuItemsExtentions = {
          "aria-setsize": menuItemAriaSetSize,
          "aria-posinset": index + 1
        };
        const clonedMenuItem = cloneMenuItem(child, focusIndex, false, "", menuItemsExtentions);
        listItems.liList.push(clonedMenuItem); // Create options list for native-select.

        listItems.optionsList.push(_jsx("option", {
          value: value,
          children: children
        }, value));
        allItems.push({
          label: children,
          isDisabled
        });
      } else if (childType.displayName === MENUITEMGROUP) {
        const {
          label: groupLabel,
          children: groupChildren,
          id: userPassedGroupId,
          wrap: wrapGroup
        } = child.props;
        allItems.push({
          label: `${selectAllText} ${groupLabel}`,
          isDisabled: false
        });
        const wrap = props.wrap;
        const groupId = userPassedGroupId || getUniqueGroupId(menuItemGroupIndex);
        menuItemGroupIndex++;
        const idForAriaDescribedBy = `${groupId}__aria-described-by`;
        const childrenValues = [];
        let checkedChildrenCount = 0,
            disabledMenuItemGroupCount = 0,

        /**
         * The item group wrap prop always takes precedence over the parent wrap prop.
         */
        shouldWrap = null;

        if (wrap || wrapGroup) {
          shouldWrap = !(wrapGroup === false);
        }

        const nestedItemsCount = React.Children.count(groupChildren);
        const nestedItems = React.Children.map(groupChildren, (childItem, index) => {
          // Increase focus index only when the list item is enabled.
          const childValue = childItem.props.value;
          const isDisabled = isMenuItemIsDisabled(childValue, childItem.props.disabled);

          if (selectedItems.includes(childValue)) {
            checkedChildrenCount++;
          }

          if (!isDisabled) {
            focusIndex++;
            childrenValues.push(childValue);
            thisRef.current.allEnabledItemValues.push(childValue);
          } else {
            disabledMenuItemGroupCount++;
            thisRef.current.groupItemDisabledCount = disabledMenuItemGroupCount;
          }

          thisRef.current.allItemsCount++;
          const menuItemsExtentions = {
            "aria-setsize": nestedItemsCount,
            // Index is 0 indexed, aria-posinset should be 1 indexed.
            "aria-posinset": index + 1,
            "aria-describedby": idForAriaDescribedBy
          };
          allItems.push({
            label: `${childItem.props.children}`,
            isDisabled
          });
          return cloneMenuItem(childItem, focusIndex, true, groupId, menuItemsExtentions);
        });
        const allChildrenChecked = Boolean(checkedChildrenCount === groupChildren.length - disabledMenuItemGroupCount);
        const hasCheckedChild = checkedChildrenCount > 0;
        const selectGroup = allChildrenChecked;
        const ariaLabel = groupAriaLabel(groupLabel, allChildrenChecked);
        const groupExtensions = {
          key: groupLabel,
          role: "option",
          // tabIndex: "-1",
          "aria-setsize": menuItemAriaSetSize,
          "aria-posinset": index + 1,
          "aria-label": ariaLabel,
          label: _jsxs(React.Fragment, {
            children: [selectAllText, " ", _jsx("span", {
              id: idForAriaDescribedBy,
              children: groupLabel
            })]
          }),
          name: groupLabel,
          "aria-selected": allChildrenChecked,
          id: groupId,
          expanded: true,
          expandable: false,
          className: "pnr-multiselect__group",
          css: menuItemCSS,
          children: nestedItems,
          wrap: shouldWrap,
          withCheckbox: true,
          onClick: () => handleGroupClick(selectGroup, childrenValues),
          isChecked: allChildrenChecked,
          isIndeterminate: Boolean(!allChildrenChecked && hasCheckedChild)
        };
        groupExtensions.menuItemGroupStyles = menuItemCSS;
        listItems.liList.push( /*#__PURE__*/React.cloneElement(child, groupExtensions)); // Create options list for native-select.

        listItems.optionsList.push(_jsx(OptGroup, {
          label: groupLabel,
          children: groupChildren
        }, groupLabel));
      }

      return null;
    });

    if (selectAllOption) {
      focusIndex++;

      const selectAllMenuItem = _jsx(MenuItem, {
        value: selectAllText,
        selectedValue: selectAllText,
        "aria-setsize": 1,
        "aria-posinset": 1,
        children: selectAllText
      });

      const clonedSelectAllMenuItem = cloneMenuItem(selectAllMenuItem, focusIndex, false, "");

      if (instructionalText) {
        listItems.liList.splice(1, 0, clonedSelectAllMenuItem);
        allItems.splice(1, 0, {
          label: `${selectAllText}`,
          isDisabled: false
        });
      } else {
        listItems.liList.unshift(clonedSelectAllMenuItem);
        allItems.unshift({
          label: `${selectAllText}`,
          isDisabled: false
        });
      }
    }

    thisRef.current.itemsMap.allItems = allItems;
    return listItems;
  };

  const listItems = children && getListFromChildren();
  /**
   * @description Computes the correct height of the list box
   * and determines the top or bottom placement of the balloon
   */

  const computeBalloonMaxHeight = () => {
    const {
      top: distanceFromTop,
      bottom,
      height
    } = thisRef.current.triggerNode.getBoundingClientRect();
    const distanceFromBottom = window.innerHeight - bottom;
    return Math.ceil(distanceFromTop > distanceFromBottom ? distanceFromTop : distanceFromBottom - height);
  };
  /**
   * Updates native select state.
   */


  const updateNativeSelectState = () => {
    // Enable the underlying input to be operated on.
    selectRef.current.removeAttribute("hidden");
    selectRef.current.style.display = "inline";
    selectRef.current.focus();
    selectRef.current.blur(); // Return the underlying input to its former state.

    selectRef.current.style.display = "none";
    selectRef.current.setAttribute("hidden", true);
  };
  /**
   * Toggles the Balloon open/close state and sets activeElement state to -1.
   */


  const toggleBalloon = e => {
    /**
     * Focus the trigger node only when the
     * click happens within the balloon.
     *
     * Focus should not be on the trigger node when
     * click happens else where on the page.
     * Meaning on any interactive element on the page.
     */
    thisRef.current.activeElementFocusable = isActiveElementFocusable();

    if (children) {
      if (open) {
        updateNativeSelectState();
        setDisplayErrors(true);
        focusOnSelectableItem(e);
      } else {
        setDisplayErrors(false);
      }

      setOpen(!open);
      setActiveIndex(-1);
    }
  };
  /**
   * This method will open the balloon when enter key is pressed.
   */


  const handleEnterKey = e => {
    toggleBalloon(e);
  };
  /*
   * Manage focus on keyDown when focus is on trigger node.
   *
   * @param e {EventFacade} The event object
   */


  const handleTriggerKeydown = e => {
    switch (e.key) {
      case KEYBOARD_KEYS.LEFT:
      case KEYBOARD_KEYS.RIGHT:
        // Prevent opening/selecting Native Select Options
        e.preventDefault();
        break;

      case KEYBOARD_KEYS.UP:
      case KEYBOARD_KEYS.DOWN:
      case KEYBOARD_KEYS.SPACE:
      case KEYBOARD_KEYS.ENTER:
        // Prevent opening Native Select Options
        e.preventDefault();
        handleEnterKey(e);
        break;

      default: // DO NOTHING

    }
  };
  /*
   * Prevents the click event from firing
   * `onKeyUp` event in order to prevent select menu
   * from opening again after selection via keyboard
   * in Firefox.
   *
   * @param e {EventFacade} The event object
   */


  const handleKeyUp = e => {
    /**
     * Every browser fires a click event on key down of space
     * on a button. This can be avoided by preventing the default
     * action on `onKeyDown` event handler.
     * But, Firefox does not respect `e.preventDefault()` on the `onKeyDown`
     * event handler (still propagates to a click event).
     * so we need to prevent the default action on `onKeyUp`
     * event handler in order to not propagate a click event.
     * This prevents the select menu from opening again after a selection
     * in Firefox.
     * https://github.com/downshift-js/downshift/issues/471
     */
    e.preventDefault();
  };
  /**
   * In the field validator component `handleBlurValidator` function identifies the
   * validity of the field by using `target` property of the event object. In this component we are
   * mimicking button to look like select, so we need to set the target property to hidden select element.
   * Furthermore it also triggers the onBlur handler passed by the user to the MultiSelect Component.
   * @param {Boolean} forceblur, flag helps to execute the blur handler.
   */


  const handleBlur = (e, forceblur) => {
    /**
     * Preventing the execution when the user
     * is clicking the `MultiSelect` trigger node to open the balloon.
     *
     * If the balloon is opened and blur is fired,
     * then the execution should not be prevented.
     */
    if (open && !forceblur) {
      return;
    } // Force the `onBlur` event on the actual <select> in order to validate.


    const {
      relatedTarget
    } = e;
    updateNativeSelectState();
    e.target !== selectRef.current && relatedTarget && relatedTarget.focus && relatedTarget.focus();
    onBlur && onBlur(e);
    /**
     * On blur, we need to show errors (if there are any).
     */

    !displayErrors && setDisplayErrors(true);
    setIsBlurred(true);
  };

  useEffect(() => {
    if (validateTimestamp) {
      /**
       * A manual validation was triggered at the form level.
       * That means we need to show error on field even if it's not
       * dirty (blurred) yet.
       */
      if (errors[name]) {
        !displayErrors && setDisplayErrors(true);
        setIsBlurred(true);
      }
    }
  }, [displayErrors, errors, name, validateTimestamp]);
  /**
   * On reset, we want to hide errors until a new blur occurs.
   */

  useEffect(() => {
    if (resetDone) {
      setDisplayErrors(false);
    }
  }, [resetDone]);
  useEffect(() => {
    if (isBlurred) {
      setIsBlurred(false);
    }
  }, [isBlurred]);
  /**
   * Show errors if the Form is being submitted even if the field
   * hasn't been blurred yet.
   */

  useEffect(() => {
    isSubmitting && setDisplayErrors(true);
  }, [isSubmitting]);
  /**
   * React Hook Form will run all validators on blur of an empty select.
   * This isn't desired, so this function creates a new set of custom
   * validators that first check for an empty value, returning valid
   * if this is the case.
   * @param {Array} customValidators Original customValidator Array.
   * @returns {Array} Array of updated validators.
   */

  const getUpdatedValidators = customValidators => {
    const newValidators = [];

    if (customValidators) {
      customValidators.forEach(customValidator => {
        const newValidator = function () {
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }

          if (args[0].length <= 0) {
            return true;
          }

          return customValidator(...args);
        };

        newValidators.push(newValidator);
      });
    }

    return newValidators;
  };
  /**
   * Registering our field with the "register" method from
   * react-hook-form.
   */


  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    requiredErrorMessage,
    customValidators: getUpdatedValidators(customValidators)
  });
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [name, unregister]);

  const setTriggerNode = nativeButton => {
    thisRef.current.triggerNode = nativeButton;
    triggerRef.current = nativeButton;

    if (userRef) {
      userRef.current = nativeButton;
    }
  };

  useOnEscape(toggleBalloon, open);
  /**
   * Handler to be executed whenever scroll event is fired on any DOM element.
   */

  const handleDocumentScrollOrClick = e => {
    const {
      target,
      type
    } = e;
    const {
      balloonNode
    } = thisRef.current;
    /**
     * Select Menu balloon should only hide when the scroll/click
     * event is fired outside the Select Menu (eg. Document, Panel).
     *
     * However, if the screen size is less than 320 (Zoom 400%),
     * scrolling is back on the menu.
     */

    if (!(window.innerWidth <= ZOOM_400_MAX_WIDTH) && balloonNode && !balloonNode.contains(target)) {
      toggleBalloon(e);
      e.stopImmediatePropagation();
    } // In order to validate on `document.body` click force the event on <select>.


    if (target !== selectRef.current && type === "click" && balloonNode && !balloonNode.contains(target)) {
      updateNativeSelectState();
      setDisplayErrors(true);
    }
  };
  /**
   * Used to differentiate between a user scrolling and the window being
   * scrolled to the menu opening.
   */


  const handleDocumentScroll = e => {
    thisRef.current.scrollCount += 1;

    if (thisRef.current.scrollCount > 2) {
      thisRef.current.scrollCount = 0;
      handleDocumentScrollOrClick(e);
    }
  };

  const handleWindowResizeDebounced = debounce(toggleBalloon, RESIZE_DEBOUNCE_TIME);
  const handleWindowScrollThrottled = throttle(handleDocumentScroll, SCROLL_THROTTLE_TIME);

  const handleChange = () => {
    const validate = {};
    return validate;
  };
  /**
   * Adds event listeners.
   */


  const addEventListeners = () => {
    const {
      scrollListenerTimeout
    } = thisRef.current;
    window.addEventListener("resize", handleWindowResizeDebounced);
    /**
     * On opening the select balloon, we need to hide the `FieldError`.
     * But, when the page is having a scrollbar and if it is scrolled to bottom
     * of the page, toggling the `FieldError` component is firing the
     * scroll event in the GC browser only. In order to prevent the
     * firing of scroll event, delaying the binding of scroll handler.
     */

    if (scrollListenerTimeout) {
      clearTimeout(scrollListenerTimeout);
    }

    thisRef.current.scrollListenerTimeout = setTimeout(() => {
      document.addEventListener("scroll", handleWindowScrollThrottled, true);
    }, SCROLL_ATTACH_TIME);
    document.addEventListener("click", handleDocumentScrollOrClick, true);
    selectRef.current.addEventListener("change", handleChange);
  };
  /**
   * Removes event listeners attached to the DOM.
   */


  const removeEventListeners = () => {
    window.removeEventListener("resize", handleWindowResizeDebounced);
    document.removeEventListener("scroll", handleWindowScrollThrottled, true);
    document.removeEventListener("click", handleDocumentScrollOrClick, true);
  };

  const getAriaLabel = label => {
    const {
      required,
      ariaLabel
    } = props; // const { selectedItems } = this.state;

    let additionalInfo, selectArialLabel;
    /**
     * HTML required and aria-required are not supported on button controls.
     * For this reason, this state information must be provided via the aria-label.
     * Storing this information in `additionalInfo` variable below.
     */

    if (required) {
      additionalInfo = REQUIRED_TEXT;
    }

    if (ariaLabel) {
      return ariaLabel(label, itemCountText(selectedItems.length), additionalInfo);
    }
    /**
     * If label is present, prepend label with selected option text.
     */


    selectArialLabel = `${label}, ${itemCountText(selectedItems.length)}`;

    if (additionalInfo) {
      selectArialLabel = `${selectArialLabel}, ${additionalInfo}`;
    } // Return a string, even if label is a number.


    return selectArialLabel;
  };

  const id = userPassedId || `pnr-multiselect-${uniqueId}`;

  const getSelectNode = () => {
    const extraParams = {};

    if (errors[name]) {
      extraParams["aria-describedby"] = errorId;
    } else if (hint) {
      extraParams["aria-describedby"] = hintId;
    }
    /**
     * Get the `aria-label` for the MultiSelect trigger button.
     * @param {String} label The label text
     * @returns String
     */


    return _jsxs(React.Fragment, {
      children: [_jsxs("select", _objectSpread(_objectSpread(_objectSpread({
        multiple: true,
        hidden: true,
        id: id,
        "aria-hidden": "true",
        className: "wf-u-visually-hidden",
        ref: e => {
          /**
           * Normally, react-hook-form does that automatically.
           * In this case, we want to share the ref with the user, so
           * we need to run this ourselves.
           *
           * See "How to share ref usage?" in the FAQs.
           * https://react-hook-form.com/faqs
           */
          inputRef && inputRef(e);
          selectRef.current = e;

          if (userRef) {
            userRef.current = e;
          }
        },
        required: required ? required : null,
        name: name
      }, elementProps), reactHookFormRest), {}, {
        children: [_jsx("option", {
          value: ""
        }), listItems && listItems.optionsList]
      })), floatingLabel && getLabel, _jsx(Button, _objectSpread(_objectSpread(_objectSpread({
        id: triggerId,
        ref: setTriggerNode,
        kind: "standard",
        onKeyDown: handleTriggerKeydown,
        onKeyUp: handleKeyUp,
        buttonStyles: selectButtonStyles,
        onClick: e => {
          /**
           * Since we only need the throttled `toggleBalloon` in iOS we do a check to avoid
           * having to use mock timers in tests.
           *
           * JSDOM always returns true for webkitOverflowScrolling.
           * To avoid this for testing we also check for clientWidth = 0 which is also
           * true by default in JSDOM.
           * We don't set this as part of `isIOS` because BarChart uses it to test some actual iOS
           * use cases in tests.
           */
          toggleBalloon(e);
        },
        onBlur: handleBlur // className={selectButtonClassName}
        ,
        disabled: isDisabled
        /**
         * AUX reasoning for using role=combobox on a <button>
         * role=combobox isn’t for buttons, but we need the role=combobox to inform
         * users that it will open a multi select box.
         * Menu Button gives the pattern of a drop down which will cause users to
         * think they can only select one item
         */
        ,
        role: "combobox",
        "aria-autocomplete": "none" // aria-owns={idListBox}
        ,
        "aria-required": required ? required : false // aria-invalid={isInvalid ? true : null}
        ,
        "aria-haspopup": "listbox",
        "aria-expanded": Boolean(open),
        "aria-label": getAriaLabel(label),
        rightIcon: floatingLabel ? _jsx(IconExpand, {
          size: expandIconSize,
          title: componentStrings.openMenu
        }) : _jsx(IconArrowClose, {
          size: "xsmall",
          title: componentStrings.openMenu
        }),
        fullWidth: true
      }, extraParams), elementProps), {}, {
        children: _jsx("span", {
          "aria-hidden": "true",
          children: itemCountText(selectedItems.length)
        })
      }))]
    });
  };
  /*
   * Focus the option based on the typed characters and nullify the search query string
   *
   */


  const focusOnKeyPress = () => {
    const character = thisRef.current.searchQuery;
    let searchMatch = false,
        currentIndex;
    /**
     * Finding index of typed character from allItems,
     * And setting active index for Keyboard Navigator.
     */

    const {
      allItems: optionsList
    } = thisRef.current.itemsMap;

    for (let index = 0; index < optionsList.length; index++) {
      const item = optionsList[index];
      const {
        label: children,
        isDisabled
      } = item;
      const characterToMatch = children.substr(0, character.length);

      if (!isDisabled && characterToMatch.toLowerCase() === character.toLowerCase()) {
        searchMatch = true;
        currentIndex = index;
        break;
      }
    }

    if (searchMatch && activeIndex !== currentIndex) {
      setActiveIndex(currentIndex);
    }

    thisRef.current.searchQuery = "";
  };

  const handleSearchTextDebounced = debounce(focusOnKeyPress, SEARCH_DEBOUNCE_TIME);
  /**
   * Add the typed characters to the search query.
   *
   * @param e {EventFacade} The event object
   */

  const handleKeyPress = e => {
    const key = e.key;
    const validKeyValues = /[a-zA-Z0-9]/;

    if (!validKeyValues.test(key)) {
      return;
    }

    thisRef.current.searchQuery += key;
    handleSearchTextDebounced();
  };

  const getCurrentActiveIndex = () => {
    const {
      allItems: optionsList
    } = thisRef.current.itemsMap;
    let currentActiveIndex;

    for (let index = 0; index < optionsList.length; index++) {
      const item = optionsList[index];
      const {
        isDisabled
      } = item;

      if (!isDisabled) {
        currentActiveIndex = index;
        break;
      }
    }

    return currentActiveIndex;
  };

  const getInstructionalId = () => {
    return instructionalText ? instructionalTextId : null;
  };
  /*
   * Manage focus on keyDown when focus is on or in Popup.
   *
   * @param e {EventFacade} The event object
   */


  const handleKeyDown = e => {
    switch (e.key) {
      case KEYBOARD_KEYS.TAB:
        // Prevent opening Native Select Options
        e.preventDefault();
        toggleBalloon(e);
        break;

      case KEYBOARD_KEYS.DOWN:
        /**
         * If ul element is not the activeElement we shouldn't do anything to let
         * KeyboardNavigator handle the action.
         */
        if (document.activeElement !== keyboardNavigatorRef.current) return; // Prevent opening Native Select Options

        e.preventDefault();
        thisRef.current.currentIndex = 0;
        setActiveIndex(getCurrentActiveIndex());
        break;

      default: // DO NOTHING

    }
  };
  /**
   * To update the active item's index based on user interaction.
   * This method will be triggered by the KeyboardNavigator
   */


  const handleActiveElementIndexChange = index => {
    thisRef.current.currentIndex = index;
    setActiveIndex(index);
  };

  if (open) {
    var _getElementWidth;

    multiSelectMenuCSS.maxHeight = computeBalloonMaxHeight() * NINETY_PERCENTAGE;
    multiSelectMenuCSS.width = (_getElementWidth = getElementWidth(thisRef.current.triggerNode)) === null || _getElementWidth === void 0 ? void 0 : _getElementWidth.width;
  }

  const balloon = children && _jsx(Popup, _objectSpread(_objectSpread({
    on: ["click"],
    ref: setBalloonRef,
    balloonContentRef: keyboardNavigatorRef,
    open: open,
    id: popupId,
    position: ["bottom center", "top center"],
    noRole: true,
    zIndex: menuzIndex,
    noArrow: true,
    onKeyPress: handleKeyPress,
    onKeyDown: handleKeyDown,
    type: "balloon",
    closeOnEscape: true,
    repositionOnResize: true,
    rootNode: wrapperNodeRef.current,
    contentStyle: {
      width: `${multiSelectMenuCSS.width}px`
    }
  }, elementProps), {}, {
    children: _jsx(KeyboardNavigator, {
      role: "listbox",
      "aria-label": componentStrings.listLabel(label),
      "aria-describedby": getInstructionalId(),
      component: "ul",
      "aria-multiselectable": true,
      css: multiSelectMenuCSS,
      mode: KEYBOARD_NAVIGATOR_MODE.VERTICAL,
      activeElement: activeIndex,
      tabIndex: "0",
      onActiveElementChange: handleActiveElementIndexChange,
      disableCyclingFocus: true,
      ref: keyboardNavigatorRef,
      className: OVERRIDE,
      children: listItems && listItems.liList
    })
  }));

  const Field = _jsxs("div", {
    css: multiSelectWrapperStyles,
    ref: wrapperNodeRef,
    children: [balloon, !floatingLabel && getLabel, getSelectNode(), _jsx(MultiSelectFooter, {
      displayErrors: displayErrors,
      errors: errors,
      errorId: errorId,
      hint: hint,
      hintId: hintId,
      hintCSS: hintCSS,
      label: label,
      name: name,
      footerErrorStyle: multiSelectFooterErrorCSS,
      footerStyle: multiSelectFooterCSS,
      open: open
    })]
  });

  useEffect(() => {
    const {
      scrollListenerTimeout
    } = thisRef.current;
    return () => {
      handleWindowResizeDebounced.cancel(); // handleSearchTextDebounced.cancel();

      handleWindowScrollThrottled.cancel();
      clearTimeout(scrollListenerTimeout);
      document.body.classList.remove(SELECT_BACKGROUND_SCROLL_CLASS);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, children]);
  useEffect(() => {
    setInternalSelectedItems(selectedItemsProp || []);
  }, [selectedItemsProp]);
  useEffect(() => {
    if (selectedItems.length > 0) {
      setValue && setValue(name, selectedItems);
      trigger && trigger();
    }
  }, [selectedItems, setValue, trigger, name]);
  useEffect(() => {
    if (open) {
      addEventListeners();
    } else {
      removeEventListeners();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [open]);
  useEffect(() => {
    var _popupRef$current;

    const trigger = thisRef.current.triggerNode;
    (_popupRef$current = popupRef.current) === null || _popupRef$current === void 0 ? void 0 : _popupRef$current.setTriggerRef(trigger);
  }, [thisRef.current.triggerNode]);
  return _jsx("div", {
    className: containerClassNames,
    css: multiSelectRoot,
    children: _jsx(React.Fragment, {
      children: Field
    })
  });
});
MultiSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Unique name identifying this MultiSelect within a form. */
  name: PropTypes.string.isRequired,

  /**
   * A function can be passed to this prop and the return value of this function will
   * be set as the `aria-label` of the MultiSelect Button. By default, it is set
   * to `${label}, ${customStrings.itemCountText}, required` if the field is required
   * and to `${label} ${customStrings.itemCountText}` if the field is not required.
   * Function passed to this prop will be called with following parameters:
   *
   * <ul>
   *  <li>
   *    `multiSelectFieldLabel`: The label of the MultiSelect field.
   *  </li>
   *  <li>
   *    `itemCountText`: Text that displays the number of items selected
   *    from the list on the MultiSelect field. By default it is equal to
   *    the value of `customStrings.itemCountText`.
   *  </li>
   *  <li>
   *    `additionalInfo`: Additional info about the state of the field.
   *     By default it is set to `required` if the field is required,
   *     and to an empty string if the field is not required.
   *  </li>
   * </ul>
   */
  ariaLabel: PropTypes.func,

  /**
   * MultiSelect accepts only [MenuItem](#/Component%20helpers/MenuItem) and
   * [MenuItemGroup](#/Component%20helpers/MenuItemGroup) components as children.
   */
  children: (props, propName, componentName) => {
    const prop = props[propName];
    let error = null;
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== MENUITEM && childrenType.displayName !== MENUITEMGROUP) {
        error = new Error(`'${componentName}' children should be of type 'MenuItem' or 'MenuItemGroup'.`);
      }
    });
    return error;
  },

  /** @ignore The CSS class name of the container that wraps both label and MultiSelect field. */
  className: PropTypes.string,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * Defaults to "Select All". Used to override the text to select all items within the dropdown.
     * Requires selectAllOption prop to be true.
     */
    selectAllText: PropTypes.string,

    /**
     * Default: (count) => "Selected (${count})" Text that is configurable to display
     * the number of items selected from the list Limit to 20 characters.
     */
    itemCountText: PropTypes.func,

    /**
     * Text appended to the `aria-label` of selected items for announcements.
     * Defaults to ", selected".
     */
    selectedText: PropTypes.string,

    /**
     * `aria-label` applied to the unordered list element, which contains
     * all of the select options.
     * Default: `(label) => "${label} options, multiple selections available."`
     */
    listLabel: PropTypes.func,

    /**
     * `aria-label` applied to the `MenuItemGroup` option.
     * Default: `(label, selected) =>
     * "Select All ${label} Group Header${selected ? ", selected" : ""}"`
     */
    groupAriaLabel: PropTypes.func,

    /**
     * Error message displayed when a required field is invalid.
     */
    requiredErrorMessage: PropTypes.string
  }),

  /**
   * Adds custom validator(s) to the MultiSelect and then calculates the validity
   * of the MultiSelect on value change.
   *
   * An array of functions that accept the value of a MultiSelect and returns an error
   * string if the MultiSelect becomes invalid.
   *
   * This prop cannot be used to check if a MultiSelect field is empty.
   * Please use the prop `required` instead.
   */
  customValidators: PropTypes.arrayOf(PropTypes.func),

  /**
   * @ignore
   * Array of item keys/ids/value to be selected by default.
   */
  defaultSelectedItems: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** Whether the MultiSelect field is disabled or not. */
  disabled: PropTypes.bool,

  /** Array of item keys/ids/value to be disabled. */
  disabledItems: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** Size of the expand icon */
  expandIconSize: PropTypes.string,

  /**
   * @ignore Callback handler to check the validation errors of the form.
   */
  // errorMessageCallback: PropTypes.func,

  /** The example/hint text for MultiSelect field. */
  hint: PropTypes.node,

  /** The MultiSelect id value. While optional, if an id is not added, one will be generated. */
  id: PropTypes.string,

  /** Used to show instructional text for selection rules */
  instructionalText: PropTypes.string,

  /** This is the label associated with the Multielect field. */
  label: PropTypes.node,

  /** Hide the label visually, but retain an accessible relationship with the MultiSelect field. */
  labelHidden: PropTypes.bool,

  /** The zIndex of the balloon in relation to other elements on the page. */
  menuzIndex: PropTypes.number,

  /**
   * @ignore
   * The width of the MultiSelect Menu Balloon in pixels.
   */
  multiSelectMenuWidth: PropTypes.number,

  /**
   * The following keys are mirror properties of the `ValidityState` interface.
   * This facilitates to customize validation messages where native html validation is used.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   *
   *   nativeValidationMessages: PropTypes.shape({
   *     badInput: PropTypes.string,
   *     patternMismatch: PropTypes.string,
   *     rangeOverflow: PropTypes.string,
   *     rangeUnderflow: PropTypes.string,
   *     stepMismatch: PropTypes.string,
   *     tooLong: PropTypes.string,
   *     tooShort: PropTypes.string,
   *     typeMismatch: PropTypes.string,
   *     valueMissing: PropTypes.string,
   *   }),
   */

  /**
   * Determines whether to add an asterisk to the left of the label of a required MultiSelect field.
   */
  noAsterisk: PropTypes.bool,

  /**
   * Callback to be executed on blur of the MultiSelect.
   * @param {EventFacade} e Receives the blur event facade as a parameter.
   * @param {Function} setErrorMessage Receives the function which when called
   * with the error message passed as a string, displays the error message
   * for the MultiSelect field.
   */
  onBlur: PropTypes.func,

  /**
   * Callback that fires when items are selected or unselected.
   * Receives an object containing an array of selected items.
   * Can be used to set the `selectedItems` prop.
   */
  onSelectedChange: requiredIf(PropTypes.func, (props, propName) => !props[propName] && props.selectedItems, "when selectedItems is supplied."),

  /** The prop will determine if Multiselect field is required. */
  required: PropTypes.bool,

  /** Shows an option to select all items from the available options in the list. */
  selectAllOption: PropTypes.bool,

  /**
   * Array of item keys/ids/value to be selected.
   * When using this prop, the component will behave as a __Controlled__ component
   * which requires the `onSelectedChange` prop to be set
   * to sync the `selectedItems` back.
   */
  selectedItems: PropTypes.arrayOf(PropTypes.string),

  /**
   * Wrap the content of each option if the content does not fit the width of the container.
   * Defaults to `true`.
   * If set to `false`, the content will be truncated with an ellipsis.
   * <br/>
   * `wrap` can also be used individually at the [MenuItem](#/Component%20helpers/MenuItem) level.
   */
  wrap: PropTypes.bool
} : {};
MultiSelect.defaultProps = {
  children: null,
  className: "",
  customValidators: null,
  disabled: false,
  disabledItems: null,
  selectedItems: null,
  defaultSelectedItems: null,
  expandIconSize: "small",
  selectAllOption: false,
  id: null,
  label: null,
  instructionalText: null,
  labelHidden: false,
  hint: null,
  name: null,
  // nativeValidationMessages: STRINGS.nativeValidationMessages,
  ariaLabel: null,
  onBlur: null,
  onSelectedChange: null,
  customStrings: null,
  required: false,
  menuzIndex: ZINDEX.BASE_BALLOON,
  noAsterisk: false,
  wrap: true,
  multiSelectMenuWidth: null // errorMessageCallback: null,

};
MultiSelect.displayName = "MultiSelect";
export default MultiSelect;